import {ProjectBudgetRes, ProjectRoleRes, ProjectSummaryGroupingRes} from "../project.contract";
import {ProjectContractDetailRes} from "../project-contract.contract";
import {InvoiceDocumentFormat} from '../../../../enums/invoice-document-format';
import {TrackerDetailRes} from "./tracker.contract";
import {ToDoGroupRes} from "../todo.contract";
import {Type} from 'class-transformer';
import {InvoiceDetailRes} from '../../cp/invoice.contract';
import {BaseTaskRes, EffortType} from '../task';
import {UsedTimeRes} from '../../resource-manager/used-time';
import moment from "moment";
import {
    ColumnType,
    FilterType, yascAutocomplete,
    yascClassDisplayName, yascDataType,
    yascDisplayName,
    yascFilterConfig,
    yascFormGrid,
    yascFormIgnore,
    YascOptionItem, yascOptionsKey,
    yascRequired,
    yascTableIgnore,
    yascTableIndex, yascTableWidth
} from '@yasc/yasc-material-components';
import {numberWithCommas} from '../../../../../apps/apps.component';
import {CompanyDetailsRes, CompanyMinimumRes} from '../../cp';
import {WorkflowDetailRes} from '../workflow.contract';
import {yascTableColumn} from '@yasc/yasc-material-components';
import {
    yascRequestOverride,
    yascTableDisplayFn, yascTableFilterType, yascTableLevelOfDetail
} from '@yasc/yasc-material-components';

@yascClassDisplayName('projectTableTitle')
export class ProjectTrackerRes extends TrackerDetailRes {
    requireProjectBudget: boolean = false;
    @yascDisplayName('general_currentWorkflow')
    @yascFormGrid({ row: 1, width: 2 })
    @yascTableIgnore(true)
    @yascTableColumn(ColumnType.Select)
    @yascRequestOverride('workflowId')
    @yascOptionsKey('workflows')
    @Type(() => WorkflowDetailRes)
    workflow!: WorkflowDetailRes;

    @yascDisplayName('projectTableVolume')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(10)
    @yascFormGrid({ row: 6, width: 1 })
    @yascRequired()
    @yascTableLevelOfDetail(1)
    projectVolumeBrutto!: number;

    @yascDisplayName('projectTableRemainingAmount')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(11)
    @yascFormGrid({ row: 6, width: 1 })
    @yascTableLevelOfDetail(1)
    projectVolumeBruttoOpen!: number;

    billingPaymentTermsDays?: number | null;
    billingPaymentDiscountPercent?: number | null;
    billingPaymentDiscountDays?: number | null;
    billingPreferredInvoiceFormat?: InvoiceDocumentFormat | null;

    @yascDisplayName('projectTableOfferNr')
    @yascTableIndex(2)
    @yascFormGrid({ row: 5, width: 1 })
    offerNr?: string;

    @yascDisplayName('projectTableOrderNr')
    @yascTableIndex(1)
    @yascFormGrid({ row: 4, width: 1 })
    orderNr?: string;

    @yascTableIgnore(true)
    @yascDisplayName('general_description')
    @yascFormGrid({ row: 7, width: 2 })
    @yascTableLevelOfDetail(3)
    comment!: string;

    private _nextBill: any;

    @yascDisplayName('crm_projects_nextBill_tableHeader')
    @yascTableIndex(12)
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(3)
    get nextBill(): any {
        return this._nextBill;
    }

    set nextBill(value: any) {
        this._nextBill = value ? moment(value) : null;
    }

    @Type(() => CompanyDetailsRes)
    @yascDisplayName('projectTableCustomer')
    @yascTableColumn(ColumnType.Autocomplete)
    @yascTableDisplayFn((value?: CompanyDetailsRes) => value?.name ?? '')
    @yascFormGrid({ row: 0, width: 2 })
    @yascRequestOverride('customerId')
    @yascTableIndex(0)
    @yascTableFilterType(FilterType.autocomplete)
    @yascAutocomplete()
    @yascOptionsKey('availableCompanies')
    @yascRequired()
    @yascFilterConfig({
        getGroup: (value?: ProjectSummaryGroupingRes) => value?.availableCompanies ?? [],
        mapToFilter: (value?: CompanyMinimumRes) =>
            value ? new YascOptionItem(value.name, value.id) : undefined as any
    })
    customer!: CompanyDetailsRes | CompanyMinimumRes;

    @Type(() => CompanyMinimumRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    timesheetCustomer?: CompanyMinimumRes | null;

    @Type(() => InvoiceDetailRes)
    invoices: InvoiceDetailRes[] = [];

    @Type(() => ProjectRoleRes)
    teamMembers: ProjectRoleRes[] = [];

    @Type(() => ProjectBudgetRes)
    budgets: ProjectBudgetRes[] = [];

    @Type(() => ProjectContractDetailRes)
    contracts: ProjectContractDetailRes[] = [];

    @Type(() => ToDoGroupRes)
    toDos: ToDoGroupRes | null = null;

    @Type(() => BaseTaskRes)
    tasks: BaseTaskRes[] = [];

    @Type(() => UsedTimeRes)
    usedTime: UsedTimeRes[] = [];

    @Type(() => EffortType)
    allowedEffortTypes: EffortType[] = [];

    sleeper!: boolean;

    color?: string;

    toMinRes() {
        const minRes = new ProjectMinRes();
        minRes.id = this.id;
        minRes.name = this.name;
        minRes.type = this.type;
        minRes.isActive = this.isActive;
        minRes.currentWorkflowStatus = this.currentWorkflowStatus;
        minRes.statusHistory = this.statusHistory;
        minRes.notes = this.notes;

        minRes.offerNr = this.offerNr;
        minRes.orderNr = this.orderNr;
        minRes.billingPaymentTermsDays = this.billingPaymentTermsDays ?? null;
        minRes.billingPaymentDiscountPercent = this.billingPaymentDiscountPercent ?? null;
        minRes.billingPaymentDiscountDays = this.billingPaymentDiscountDays ?? null;
        minRes.billingPreferredInvoiceFormat = this.billingPreferredInvoiceFormat ?? null;
        minRes.comment = this.comment;
        minRes.teamMemberCount = this.teamMembers?.length ?? 0;
        minRes.sleeper = this.sleeper;
        minRes.color = this.color;
        minRes.nextBill = this.nextBill;
        minRes.projectVolumeBrutto = this.projectVolumeBrutto;
        minRes.projectVolumeBruttoOpen = this.projectVolumeBruttoOpen;

        if (this.customer) {
            const customer = new CompanyMinimumRes();
            customer.id = this.customer.id;
            customer.name = this.customer.name;
            minRes.customer = customer;
        }

        if (this.timesheetCustomer) {
            const timesheetCustomer = new CompanyMinimumRes();
            timesheetCustomer.id = this.timesheetCustomer.id;
            timesheetCustomer.name = this.timesheetCustomer.name;
            minRes.timesheetCustomer = timesheetCustomer;
        }

        return minRes;
    }
}

@yascClassDisplayName('projectTableTitle')
export class ProjectMinRes extends TrackerDetailRes {
    requireProjectBudget: boolean = false;
    @Type(() => CompanyMinimumRes)
    @yascDisplayName('projectTableCustomer')
    @yascTableColumn(ColumnType.Autocomplete)
    @yascTableDisplayFn((value?: CompanyMinimumRes) => value?.name)
    @yascFormGrid({ row: 0, width: 2 })
    @yascRequestOverride('customerId')
    @yascTableFilterType(FilterType.autocomplete)
    @yascAutocomplete()
    @yascOptionsKey("availableCompanies")
    @yascRequired()
    @yascTableIndex(0)
    @yascFilterConfig({
        getGroup: (value?: ProjectSummaryGroupingRes) => value?.availableCompanies ?? [],
        mapToFilter: (value?: CompanyMinimumRes) =>
            value ? new YascOptionItem(value.name, value.id) : undefined as any
    })
    customer!: CompanyMinimumRes;

    @Type(() => CompanyMinimumRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    timesheetCustomer?: CompanyMinimumRes | null;

    @yascDisplayName('projectTableOrderNr')
    @yascTableIndex(2)
    @yascFormGrid({ row: 4, width: 1 })
    orderNr?: string;

    billingPaymentTermsDays?: number | null;
    billingPaymentDiscountPercent?: number | null;
    billingPaymentDiscountDays?: number | null;
    billingPreferredInvoiceFormat?: InvoiceDocumentFormat | null;

    @yascDisplayName('projectTableOfferNr')
    @yascTableIndex(3)
    @yascFormGrid({ row: 5, width: 1 })
    offerNr?: string;

    @yascTableIgnore(true)
    @yascDisplayName('general_description')
    @yascFormGrid({ row: 7, width: 2 })
    @yascTableLevelOfDetail(3)
    comment?: string;

    @yascDisplayName('projectTabTeamTitle')
    @yascTableIgnore(true)
    @yascFormIgnore()
    @yascFormGrid({ row: 1, width: 1 })
    teamMemberCount!: number;

    private _nextBill: any;

    @yascDisplayName('crm_projects_nextBill_tableHeader')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Date)
    @yascTableLevelOfDetail(2)
    get nextBill(): any {
        return this._nextBill;
    }

    set nextBill(value: any) {
        this._nextBill = value ? moment(value) : null;
    }

    @yascDisplayName('projectTableVolume')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(5)
    @yascFormGrid({ row: 6, width: 1 })
    @yascTableLevelOfDetail(1)
    projectVolumeBrutto!: number;

    @yascDisplayName('projectTableRemainingAmount')
    @yascTableDisplayFn((value: number) => numberWithCommas(value))
    @yascTableIndex(6)
    @yascFormGrid({ row: 6, width: 1 })
    @yascTableLevelOfDetail(1)
    projectVolumeBruttoOpen!: number;

    @yascTableIgnore(true)
    @yascFormIgnore()
    sleeper!: boolean;

    @yascDisplayName('general_color')
    @yascFormGrid({ row: 5, width: 1 })
    @yascTableLevelOfDetail(2)
    @yascTableIgnore(true)
    color?: string;

    @yascDisplayName('projectTableStart')
    @yascTableIgnore(true)
    @yascFormGrid({ row: 8, width: 1 })
    start?: string;

    @yascDisplayName('projectTableEnd')
    @yascTableIgnore(true)
    @yascFormGrid({ row: 8, width: 1 })
    end?: string;
}